import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';
import { get, isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
// eslint-disable-next-line no-restricted-imports -- ErrorPage provides full page layout (header/footer), no CDS equivalent
import ErrorPage from 'bundles/coursera-ui/components/rich/ErrorPage';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts/enterprise-admin-contracts-index';
import EnterpriseHomeAccessErrorPage from 'bundles/program-home/components/enterprise-home/EnterpriseHomeAccessErrorPage';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useHasThirdPartyOrgError,
  useOrgContracts,
  useThirdPartyOrganization,
} from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import useIsOrgAllLimitedContent from 'bundles/unified-common/hooks/useIsOrgAllLimitedContent';
import EnterpriseIntegrations from 'bundles/unified-common/providers/org-scoped/guards/EnterpriseIntegrations';
import {
  isOrgScopedBrowsePage,
  isOrgScopedHomePage,
  isOrgScopedMyLearningPage,
  isOrgScopedSearchPage,
} from 'bundles/unified-common/utils/orgScopedRouteUtils';
import { OrgScopedArchivedDenyModal } from 'bundles/unified-home-common/components/enterprise/modals/OrgScopedArchivedDenyModal';

type Props = {
  children: React.ReactNode;
};

const EnterpriseOrgScopedPageGuardInternal = ({ children }: Props) => {
  const features = useEnterpriseProductFeatures();
  const orgContracts = useOrgContracts();
  const thirdPartyOrg = useThirdPartyOrganization();
  const hasThirdPartyOrgError = useHasThirdPartyOrgError();
  const isUserAuthenticated = isAuthenticatedUser();
  const location = useLocation();
  const { isAllLimitedContent, isLoading: isLimitedContentLoading } = useIsOrgAllLimitedContent();

  // Invited-only org: deny access if the user is not an authenticated org member/admin/invitee.
  // TODO: Once the org-level membership API is available, add checks for isOrgMember, isOrgAdmin, and isInvited
  // to allow authorized users through (mirroring the program-level logic in EnterpriseUserStateCheckWrapper).
  if (features?.enableInvitedOnlyOrg && !isUserAuthenticated) {
    return (
      <ErrorPage>
        <EnterpriseHomeAccessErrorPage isLoggedIn={false} thirdPartyOrganizationId={thirdPartyOrg?.id} />
      </ErrorPage>
    );
  }

  // Show archived deny modal if org contracts are paused or terminated
  if (allContractsHardPausedOrTerminated(orgContracts)) {
    return <OrgScopedArchivedDenyModal userId={get()?.id} />;
  }
  // If no org found, show 404
  // (loading states already handled above)
  if (!thirdPartyOrg || hasThirdPartyOrgError) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      return <NotFoundPage />;
    }
  }
  // TODO: privacy notice agreement

  // Limited-content guard: when all resolved programs are limited content (SMALL_SUBSET),
  // browse has no meaningful content (only Coursera recs/general content) for any user,
  // and logged-out users additionally can't access search or the home page catalog.
  // Block pages that will redirect while loading to prevent a 404/content flash.
  if (isLimitedContentLoading) {
    if (isOrgScopedBrowsePage(location.pathname)) {
      return null;
    }
    if (!isUserAuthenticated && isOrgScopedSearchPage(location.pathname)) {
      return null;
    }
  }
  if (isAllLimitedContent) {
    // Browse only has Coursera recs/general content — redirect all users to org home
    if (isOrgScopedBrowsePage(location.pathname)) {
      redirectClientOrServer(`/organizations/${thirdPartyOrg.slug}`);
      return null;
    }
    if (!isUserAuthenticated) {
      if (isOrgScopedSearchPage(location.pathname)) {
        redirectClientOrServer(`/organizations/${thirdPartyOrg.slug}`);
        return null;
      }
      if (isOrgScopedHomePage(location.pathname)) {
        return (
          <ErrorPage>
            <EnterpriseHomeAccessErrorPage isLoggedIn={false} thirdPartyOrganizationId={thirdPartyOrg.id} />
          </ErrorPage>
        );
      }
    }
  }

  // My Learning requires authentication — redirect logged-out users to org home.
  if (isOrgScopedMyLearningPage(location.pathname) && !isUserAuthenticated) {
    redirectClientOrServer(`/organizations/${thirdPartyOrg.slug}`);
    return null;
  }

  return <>{children}</>;
};

export const EnterpriseOrgScopedPageGuard = ({ children }: Props) => (
  <EnterpriseIntegrations>
    <EnterpriseOrgScopedPageGuardInternal>{children}</EnterpriseOrgScopedPageGuardInternal>
  </EnterpriseIntegrations>
);
